/**
 * Module: `ui-helper/text.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

/**
 * Matches from the first ! to the last ! (greedy), so tokens like !YOLO! EJ!
 * are removed in one go—everything between the first and last ! inclusive.
 */
const EXCLAMATION_TOKEN_REGEX = /^!.*!/;

/**
 * Strips exclamation-wrapped tokens (!...!) from a string for display.
 * Use when rendering conversation list titles.
 */
export function stripExclamationTokens(text: string): string {
  if (!text || typeof text !== "string") return text;
  return text.replace(EXCLAMATION_TOKEN_REGEX, "").trim();
}

/**
 * Matches Dust mention prefixes like :mention[SidoniSearchJSON_minimal_retrieval]{sId=S8LQ4wclOV}
 * that may appear in conversation titles and question content from the Dust API or DB.
 */
const DUST_MENTION_PREFIX_REGEX = /:mention\[[^\]]*\]\{[^}]*\}/g;

/**
 * Strips Dust mention prefixes from a string for display.
 * Use when rendering conversation title or question content.
 */
export function stripDustMentionPrefix(text: string): string {
  if (!text || typeof text !== "string") return text;
  return text.replace(DUST_MENTION_PREFIX_REGEX, "").trim();
}

/**
 * Capitalizes the first letter of a string
 * Handles edge cases like empty strings, whitespace, and non-letter first characters
 *
 * @param text - The text to capitalize
 * @returns The text with its first letter capitalized
 */
export function capitalizeFirstLetter(text: string): string {
  if (!text || typeof text !== "string") return text;
  const trimmed = text.trimStart();
  if (!trimmed) return text;

  // Find the first letter character (skipping any leading punctuation/numbers)
  const firstLetterIndex = trimmed.search(/[a-zA-ZÀ-ÿ]/);
  if (firstLetterIndex === -1) return text;

  const leadingWhitespace = text.slice(0, text.length - trimmed.length);
  const beforeLetter = trimmed.slice(0, firstLetterIndex);
  const firstLetter = trimmed.charAt(firstLetterIndex).toUpperCase();
  const rest = trimmed.slice(firstLetterIndex + 1);

  return leadingWhitespace + beforeLetter + firstLetter + rest;
}
